import { ApolloProvider } from '@apollo/client';
import '@material-symbols/font-500/rounded.css';
import {
  API_HOST,
  BRAND_NAME,
  ENVIRONMENT,
  LAUNCH_DARKLY_SDK_KEY,
  LoadingPage,
  sentryConfigs,
  SharedAppProviders,
} from '@pm/core';
import { Toaster } from '@pm/ui';
import { GoogleOAuthProvider } from '@react-oauth/google';
import * as Sentry from '@sentry/react';
import { ComponentProps, ReactNode, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router';
import { apolloClient } from './api/client';
import App from './apps';
import { FreddieAppErrorBoundary } from './components/FreddieAppErrorBoundary';
import {
  awaitsUsableForm,
  describeIntakePageload,
} from './utilities/intakeLoadTracking';
import { sendPageLoadDataToAnalytics } from './utilities/pagePerformance';
import SEO from './utilities/seo';

// translations
import './localization/i18n';

// css files
import '@pm/ui/themes/freddie.css';
import '@pm/ui/themes/global.css';
import './tailwind.css';

console.log(
  'Environment variables:',
  import.meta.env.VITE_ENVIRONMENT,
  import.meta.env.VITE_GIT_PULL_REQUEST_ID,
  process.env.GIT_COMMIT_HASH,
);

const environment = {
  prod: 'prod',
  dev: 'dev',
};

const isProdEnv = import.meta.env.VITE_ENVIRONMENT === 'production';

const GOOGLE_AUTH_CLIENT_ID = import.meta.env.VITE_GOOGLE_AUTH_CLIENT_ID ?? '';

// Google's Identity Services throws "Missing required parameter client_id" when the
// provider mounts with an empty client_id. Patient Google OAuth (PDE-650) was reverted
// and the env var stays unset outside prod, so only mount the provider when a client_id
// is actually configured — it lights back up on its own when the feature returns.
const GoogleOAuthWrapper = ({
  clientId,
  children,
}: {
  clientId: string;
  children: ReactNode;
}) =>
  clientId ? (
    <GoogleOAuthProvider clientId={clientId}>{children}</GoogleOAuthProvider>
  ) : (
    <>{children}</>
  );

const pageAwaitsUsableForm =
  awaitsUsableForm(window.location.pathname) && !localStorage.getItem('jwt');

performance.mark('freddie-app-boot');

// initialize our Sentry crash reporting tool
if (import.meta.env.PROD) {
  const sentryEnvironment = isProdEnv ? environment.prod : environment.dev;
  const networkDetailAllowUrls = [`${API_HOST}/graphql`];

  Sentry.init({
    dsn: 'https://2659d8349ea649a98bf09338af64d8fa@o371410.ingest.sentry.io/5382518',
    environment: sentryEnvironment,
    integrations: [
      Sentry.browserTracingIntegration({
        enableReportPageLoaded: pageAwaitsUsableForm,
      }),
      Sentry.userTimingIntegration(),
      Sentry.consoleLoggingIntegration({
        levels: ['debug', 'log', 'warn', 'error'],
      }),
    ],
    replaysSessionSampleRate:
      sentryEnvironment === environment.prod ? 1.0 : 0.0,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: sentryEnvironment === environment.prod ? 0.5 : 1.0,
    tracePropagationTargets: [
      'https://api.purposemed.com/graphql',
      'https://api-staging.gofreddie.com/graphql',
    ],
    beforeSendSpan: sendPageLoadDataToAnalytics,
    ...sentryConfigs,
  });

  if (pageAwaitsUsableForm) {
    describeIntakePageload();
  }

  void import('./utilities/sentryReplay')
    .then(({ addSentryReplay }) =>
      addSentryReplay({
        maskAllText: false,
        maskAllInputs: sentryEnvironment === environment.prod,
        mask: ['.sensitive', '.sensitive *'],
        networkDetailAllowUrls,
      }),
    )
    .catch(() => undefined);
}

const ENV_OPTIONS: Omit<
  ComponentProps<typeof SharedAppProviders>,
  'children'
> = {
  brand: BRAND_NAME.Freddie,
  apiHost: API_HOST,
  environment: ENVIRONMENT,
  launchDarklySDKKey: LAUNCH_DARKLY_SDK_KEY,
  formattingContext: 'patient',
  enableGoogleAnalyticsTracking: true,
};

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <BrowserRouter>
      <FreddieAppErrorBoundary>
        <HelmetProvider>
          <Suspense fallback={<LoadingPage />}>
            <ApolloProvider client={apolloClient}>
              <GoogleOAuthWrapper clientId={GOOGLE_AUTH_CLIENT_ID}>
                <SharedAppProviders {...ENV_OPTIONS}>
                  <SEO />
                  <Toaster>
                    <App />
                  </Toaster>
                </SharedAppProviders>
              </GoogleOAuthWrapper>
            </ApolloProvider>
          </Suspense>
        </HelmetProvider>
      </FreddieAppErrorBoundary>
    </BrowserRouter>
  </StrictMode>,
);
